import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Toast — the StatesProgress "Anywhere, run finished" card: 32 px icon well,
 * bold title, muted one-liner, optional action link on the right.
 *
 * Imperative API (works from anywhere, no provider needed):
 *   import { toast } from '../ui/components';
 *   toast.show({ title: 'Backtest finished', body: 'RSI dip, SPY: holds up', tone: 'ok',
 *                icon: 'B', action: { label: 'View', onClick } });
 *   toast.warn('Order blocked: daily loss limit');   // shorthands: ok/info/warn/error
 * Options: title, body, tone ('info' | 'ok' | 'warn' | 'err'), icon (1–2 chars
 * or a node), action { label, onClick }, duration ms (default 6000; 0 = sticky),
 * id (re-using an id replaces that toast instead of stacking a duplicate).
 *
 * <ToastViewport /> renders the stack (bottom-right; above the phone tab bar).
 * The app shell mounts one; mount another only outside the shell.
 */
let seq = 0;
let items = [];
const timers = new Map();
const listeners = new Set();
const emit = () => listeners.forEach((l) => l(items));

function dismiss(id) {
  if (timers.has(id)) { clearTimeout(timers.get(id)); timers.delete(id); }
  items = items.filter((t) => t.id !== id);
  emit();
}

function show(opts) {
  const o = typeof opts === 'string' ? { title: opts } : opts || {};
  seq += 1;
  const id = o.id != null ? String(o.id) : `t${seq}`;
  const entry = { tone: 'info', duration: 6000, ...o, id };
  items = [...items.filter((t) => t.id !== id), entry].slice(-4);
  emit();
  // Re-using an id replaces the toast AND restarts its clock: the old timer
  // must not dismiss the replacement early.
  if (timers.has(id)) { clearTimeout(timers.get(id)); timers.delete(id); }
  if (entry.duration > 0) timers.set(id, setTimeout(() => dismiss(id), entry.duration));
  return id;
}

const shorthand = (tone, icon) => (title, extra = {}) => show({ title, tone, icon, ...extra });

export const toast = {
  show,
  dismiss,
  info: shorthand('info', 'i'),
  ok: shorthand('ok', '✓'),
  warn: shorthand('warn', '!'),
  error: shorthand('err', '!'),
  /** test helper */
  _reset() { timers.forEach((t) => clearTimeout(t)); timers.clear(); items = []; emit(); },
};

export function Toast({ title, body, tone = 'info', icon, action, onClose }) {
  const glyph = icon != null ? icon : tone === 'ok' ? '✓' : tone === 'info' ? 'i' : '!';
  return (
    <div className={`ui-toast ui-toast--${tone}`} role={tone === 'err' || tone === 'warn' ? 'alert' : 'status'}>
      <span className="ui-toast__ic" aria-hidden="true">{glyph}</span>
      <div className="ui-toast__main">
        {title ? <b className="ui-toast__title">{title}</b> : null}
        {body ? <div className="ui-toast__body">{body}</div> : null}
      </div>
      {action ? (
        <button type="button" className="ui-toast__action" onClick={() => { if (action.onClick) action.onClick(); if (onClose) onClose(); }}>
          {action.label}
        </button>
      ) : null}
      {onClose && !action ? (
        <button type="button" className="ui-x" aria-label="Dismiss" onClick={onClose} style={{ width: 24, height: 24 }}>
          ×
        </button>
      ) : null}
    </div>
  );
}

export function ToastViewport() {
  const [list, setList] = useState(items);
  useEffect(() => {
    listeners.add(setList);
    setList(items);
    return () => listeners.delete(setList);
  }, []);
  if (typeof document === 'undefined' || list.length === 0) return null;
  return createPortal(
    <div className="ui-root ui-toasts" aria-live="polite">
      {list.map((t) => (
        <Toast key={t.id} {...t} onClose={() => dismiss(t.id)} />
      ))}
    </div>,
    document.body,
  );
}

export default toast;
